import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SITE } from "../constants/site";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";

export default function Terms() {
    return (
        <div data-testid="terms-page" className="min-h-screen bg-background">
            <Navbar />

            <section className="pt-32 pb-12 border-b border-slate-200 bg-white">
                <div className="max-w-4xl mx-auto px-6 sm:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                        <FileText className="h-4 w-4 text-brand-purple" />
                        VERSION 1.0
                    </div>
                    <h1 className="font-display mt-5 text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
                        Terms of Service
                    </h1>
                    <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
                        {SITE.appName} by {SITE.company} · Republic of South
                        Africa
                    </p>
                    <p className="mt-2 text-sm text-slate-500">
                        Effective date: {SITE.effectiveDate}
                    </p>
                </div>
            </section>

            <section className="py-16">
                <div className="max-w-4xl mx-auto px-6 sm:px-8">
                    <article
                        data-testid="terms-content"
                        className="space-y-12 text-slate-700 leading-relaxed"
                    >
                        <Block id="acceptance" title="1. Acceptance">
                            <p>
                                By creating a {SITE.appName} account you agree
                                to these Terms and to our{" "}
                                <Link
                                    to="/privacy"
                                    className="text-brand-purple font-medium hover:underline"
                                >
                                    Privacy Policy
                                </Link>
                                .
                            </p>
                        </Block>

                        <Block
                            id="service-description"
                            title="2. Service Description"
                        >
                            <p>
                                {SITE.appName} is a family-first management
                                app. Features are offered as-is; availability
                                may vary as we release updates and improve the
                                service.
                            </p>
                        </Block>

                        <Block
                            id="account-responsibilities"
                            title="3. Account Responsibilities"
                        >
                            <p>
                                You are responsible for keeping your login
                                credentials secure and for the accuracy of the
                                data you enter. You agree not to misuse the
                                service or infringe on the rights of others.
                            </p>
                        </Block>

                        <Block
                            id="subscriptions-billing"
                            title="4. Subscriptions & Billing"
                        >
                            <p>
                                Paid tiers renew automatically at the start of
                                each billing cycle unless cancelled. Purchases
                                made on Android are processed by Google Play
                                Billing and are subject to Google Play's refund
                                policies. Refunds follow the Consumer
                                Protection Act (South Africa).{" "}
                                <strong>"Founding Family"</strong> is a
                                lifetime deal limited to the first 500
                                households.
                            </p>
                        </Block>

                        <Block
                            id="medical-disclaimer"
                            title="5. Medical Disclaimer"
                        >
                            <p>
                                {SITE.appName} is an information management
                                tool. It is <strong>NOT a medical device</strong>{" "}
                                and does <strong>NOT provide medical advice, diagnosis or treatment</strong>.
                                Always consult a qualified healthcare
                                professional in an emergency.
                            </p>
                        </Block>

                        <Block
                            id="limitation-liability"
                            title="6. Limitation of Liability"
                        >
                            <p>
                                To the fullest extent permitted by law,{" "}
                                {SITE.company} is not liable for indirect,
                                incidental, or consequential damages arising
                                from your use of {SITE.appName}.
                            </p>
                        </Block>

                        <Block id="termination" title="7. Termination">
                            <p>
                                You may delete your account at any time — see{" "}
                                <Link
                                    to="/account-deletion"
                                    className="text-brand-purple font-medium hover:underline"
                                >
                                    Account &amp; Data Deletion
                                </Link>
                                . We may suspend or terminate accounts that
                                violate these Terms.
                            </p>
                        </Block>

                        <Block id="governing-law" title="8. Governing Law">
                            <p>
                                These Terms are governed by the laws of the
                                Republic of South Africa.
                            </p>
                        </Block>

                        <Block id="contact" title="9. Contact">
                            <div className="not-prose rounded-xl bg-slate-50 border border-slate-200 p-5 text-sm">
                                <div className="font-semibold text-slate-900">
                                    {SITE.company}
                                </div>
                                <div className="mt-1">{SITE.address}</div>
                                <div className="mt-2">
                                    <a
                                        className="text-brand-purple font-medium hover:underline"
                                        href={`mailto:${SITE.supportEmail}`}
                                    >
                                        {SITE.supportEmail}
                                    </a>
                                </div>
                            </div>
                        </Block>
                    </article>
                </div>
            </section>

            <Footer />
        </div>
    );
}

const Block = ({ id, title, children }) => (
    <section id={id} data-testid={`section-${id}`} className="scroll-mt-28">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {title}
        </h2>
        <div className="mt-4 prose prose-slate max-w-none prose-p:leading-relaxed prose-strong:text-slate-900">
            {children}
        </div>
    </section>
);
